// =============================================
// Parent Registration Flow - רישום הורה חדש
// Kipikid - מערכת נוכחות ילדים בגנים
// =============================================

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone,
  User,
  Mail,
  MapPin,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Globe,
  Bell,
  FileText,
  Loader2,
} from 'lucide-react';
import { API_BASE_URL } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import FormLabel from '../../components/common/FormLabel';
import HelperText from '../../components/common/HelperText';
import HelpTooltip from '../../components/common/HelpTooltip';
import { parentRegistrationHelpTexts } from '../../constants/helpTexts';
import { parentColors } from '../../styles/colors';

const colors = {
  ...parentColors,
  background: '#f9fafb',
};

interface RegistrationData {
  phone: string;
  otp: string;
  firstName: string;
  lastName: string;
  email: string;
  preferredLanguage: string;
  notificationMethod: string;
  address: {
    city: string;
    street: string;
    houseNumber: string;
    floor: string;
    apartment: string;
  };
  agreementsAccepted: boolean;
}

interface PendingInvitation {
  inviterName?: string;
  invitedName?: string;
  childrenNames?: string[];
  token?: string;
}

export default function ParentRegistration() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setToken } = useAuthStore();
  const inviteToken = searchParams.get('token');

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [pendingInvitation, setPendingInvitation] = useState<PendingInvitation | null>(null);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [redirectAfterRegister, setRedirectAfterRegister] = useState<string | null>(null);
  const [phoneFromLoginFlow, setPhoneFromLoginFlow] = useState<string | null>(null);
  const [otpFromLoginFlow, setOtpFromLoginFlow] = useState<string | null>(null);
  const [agreementContent, setAgreementContent] = useState<Array<{ type: number; version: string; title: string; content: string }>>([]);
  const [loadingAgreement, setLoadingAgreement] = useState(false);

  const [data, setData] = useState<RegistrationData>({
    phone: '',
    otp: '',
    firstName: '',
    lastName: '',
    email: '',
    preferredLanguage: 'he',
    notificationMethod: 'whatsapp',
    address: {
      city: '',
      street: '',
      houseNumber: '',
      floor: '',
      apartment: '',
    },
    agreementsAccepted: false,
  });

  // Check for registration data from login flow (new user redirect)
  useEffect(() => {
    const registrationDataStr = sessionStorage.getItem('kipikid-registration');
    if (registrationDataStr) {
      try {
        const registrationData = JSON.parse(registrationDataStr);
        console.log('Found registration data from login flow:', registrationData);

        // Store invitation info first
        if (registrationData.pendingInvitation) {
          setPendingInvitation(registrationData.pendingInvitation);
        }

        // Store phone separately to ensure it's available for createUser
        if (registrationData.phone) {
          setPhoneFromLoginFlow(registrationData.phone);
        }

        // Store OTP for getting token after user creation
        if (registrationData.otp) {
          setOtpFromLoginFlow(registrationData.otp);
        }

        // Pre-fill all data in a single setData call to avoid race conditions
        setData(prev => {
          const newData = { ...prev };

          // Pre-fill phone number
          if (registrationData.phone) {
            newData.phone = registrationData.phone;
          }

          // Pre-fill fields from invitation
          const inv = registrationData.pendingInvitation;
          if (inv) {
            // Use separate firstName/lastName if available
            if (inv.invitedFirstName) newData.firstName = inv.invitedFirstName;
            if (inv.invitedLastName) newData.lastName = inv.invitedLastName;

            // Fallback to parsing combined name
            if (!newData.firstName && !newData.lastName && inv.invitedName) {
              const nameParts = inv.invitedName.trim().split(' ', 2);
              newData.firstName = nameParts[0] || '';
              newData.lastName = nameParts.length > 1 ? nameParts[1] : '';
            }

            // Pre-fill email if available
            if (inv.invitedEmail) newData.email = inv.invitedEmail;
          }

          console.log('Setting registration data:', newData);
          return newData;
        });

        // Set states after data is set
        if (registrationData.phone) {
          setPhoneVerified(true);
          setOtpSent(true);
          setStep(2); // Skip to personal details step
        }

        // Store redirect URL if provided
        if (registrationData.redirectUrl) {
          setRedirectAfterRegister(registrationData.redirectUrl);
        }

        // Clear session storage after reading
        sessionStorage.removeItem('kipikid-registration');
      } catch (e) {
        console.error('Error parsing registration data:', e);
      }
    }
  }, []);

  // Countdown for OTP resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const sendOtp = async () => {
    if (!data.phone || data.phone.length < 9) {
      setError('נא להזין מספר טלפון תקין');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Phone: data.phone }),
      });

      const result = await response.json();

      if (result.success) {
        setOtpSent(true);
        setCountdown(60);
      } else {
        setError(result.message || 'שגיאה בשליחת קוד האימות');
      }
    } catch {
      setError('שגיאה בשליחת קוד האימות');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!data.otp || data.otp.length !== 6) {
      setError('נא להזין קוד אימות בן 6 ספרות');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Phone: data.phone, Otp: data.otp }),
      });

      const result = await response.json();

      if (result.success) {
        // Check if user exists
        if (result.data?.user) {
          // Existing user - redirect to home
          if (result.data.accessToken) setToken(result.data.accessToken);
          navigate('/parent/children');
        } else {
          // New user - continue registration
          setStep(2);
        }
      } else {
        setError(result.message || 'קוד אימות שגוי');
      }
    } catch {
      setError('שגיאה באימות');
    } finally {
      setLoading(false);
    }
  };

  const createUser = async () => {
    // Use phoneFromLoginFlow as fallback if data.phone is not set
    const phoneToUse = data.phone || phoneFromLoginFlow;

    if (!phoneToUse) {
      setError('מספר טלפון חסר - נא לחזור לשלב הראשון');
      return;
    }
    if (!data.firstName || !data.lastName) {
      setError('נא למלא שם פרטי ושם משפחה');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const requestBody = {
        phone: phoneToUse,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email || undefined,
        preferredLanguage: data.preferredLanguage,
        notificationMethod: data.notificationMethod,
        address: data.address.city
          ? data.address
          : undefined,
      };
      console.log('Creating user with data:', requestBody);

      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (result.success) {
        // After creating user, call verify-otp to get a token
        const otpToUse = data.otp || otpFromLoginFlow;
        if (otpToUse && phoneToUse) {
          try {
            const tokenResponse = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ Phone: phoneToUse, Otp: otpToUse }),
            });
            const tokenResult = await tokenResponse.json();
            if (tokenResult.success && tokenResult.data?.accessToken) {
              setToken(tokenResult.data.accessToken);
            }
          } catch (e) {
            console.error('Failed to get token after user creation:', e);
          }
        }
        setStep(4); // Go to agreements
      } else {
        setError(result.message || 'שגיאה ביצירת המשתמש');
      }
    } catch {
      setError('שגיאה ביצירת המשתמש');
    } finally {
      setLoading(false);
    }
  };

  // Fetch agreement content when reaching step 4
  useEffect(() => {
    if (step === 4) {
      const fetchAgreements = async () => {
        setLoadingAgreement(true);
        try {
          const token = useAuthStore.getState().token;
          const res = await fetch(`${API_BASE_URL}/agreements/to-sign`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const result = await res.json();
          if (result.success && result.data) {
            setAgreementContent(result.data.filter((a: any) => a.required && !a.alreadySigned));
          }
        } catch {
          console.error('Failed to load agreement content');
        } finally {
          setLoadingAgreement(false);
        }
      };
      fetchAgreements();
    }
  }, [step]);

  const acceptAgreements = async () => {
    setLoading(true);
    setError(null);

    try {
      // Sign all required agreements from API
      const token = useAuthStore.getState().token;
      for (const agreement of agreementContent) {
        await fetch(`${API_BASE_URL}/agreements/sign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            agreementType: agreement.type,
            version: agreement.version,
          }),
        });
      }

      // If there's an invite token (from URL or from login flow), accept it
      const tokenToAccept = inviteToken || pendingInvitation?.token;
      if (tokenToAccept) {
        await fetch(`${API_BASE_URL}/invitations/${tokenToAccept}/accept`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${useAuthStore.getState().token}`,
          },
        });
      }

      // Navigate to redirect URL if provided, otherwise to children page
      navigate(redirectAfterRegister || '/parent/children');
    } catch {
      setError('שגיאה באישור ההסכמים');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    fontSize: '16px',
    border: `2px solid ${colors.border}`,
    borderRadius: '12px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    direction: 'rtl' as const,
    transition: 'border-color 0.2s',
  };

  const labelStyle = {
    fontSize: '14px',
    fontWeight: '500' as const,
    color: colors.text,
    marginBottom: '8px',
    display: 'block',
  };

  const buttonStyle = {
    width: '100%',
    padding: '14px',
    fontSize: '16px',
    fontWeight: '600' as const,
    color: 'white',
    backgroundColor: colors.primary,
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${colors.primary} 0%, #ea580c 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          width: '100%',
          maxWidth: '450px',
          backgroundColor: 'white',
          borderRadius: '24px',
          padding: '32px',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '20px',
              backgroundColor: colors.primaryLight,
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <User size={40} color={colors.primary} />
          </div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: colors.text, marginBottom: '8px' }}>
            {step === 1 && 'כניסה / הרשמה'}
            {step === 2 && 'פרטים אישיים'}
            {step === 3 && 'העדפות'}
            {step === 4 && 'אישור הסכמים'}
          </h1>
          <p style={{ fontSize: '14px', color: colors.textLight }}>
            {step === 1 && 'הזן את מספר הטלפון שלך'}
            {step === 2 && 'מלא את הפרטים האישיים שלך'}
            {step === 3 && 'בחר את ההעדפות שלך'}
            {step === 4 && 'אנא קרא ואשר את ההסכמים'}
          </p>
        </div>

        {/* Progress Steps */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '24px' }}>
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              style={{
                width: s === step ? '24px' : '8px',
                height: '8px',
                borderRadius: '4px',
                backgroundColor: s <= step ? colors.primary : colors.border,
                transition: 'all 0.3s',
              }}
            />
          ))}
        </div>

        {/* Error Message */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              style={{
                backgroundColor: '#fef2f2',
                color: '#dc2626',
                padding: '12px 16px',
                borderRadius: '12px',
                marginBottom: '16px',
                fontSize: '14px',
                textAlign: 'center',
              }}
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step 1: Phone & OTP */}
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <FormLabel text="מספר טלפון" helpText={parentRegistrationHelpTexts.phone.tooltip} />
              <div style={{ position: 'relative' }}>
                <Phone
                  size={20}
                  color={colors.textLight}
                  style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }}
                />
                <input
                  type="tel"
                  placeholder="05X-XXX-XXXX"
                  value={data.phone}
                  onChange={(e) => setData({ ...data, phone: e.target.value })}
                  style={{ ...inputStyle, paddingRight: '44px', direction: 'ltr', textAlign: 'center' }}
                  disabled={otpSent}
                />
              </div>
              <HelperText text={parentRegistrationHelpTexts.phone.helper} />
            </div>

            {!otpSent ? (
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={sendOtp}
                disabled={loading}
                style={{ ...buttonStyle, opacity: loading ? 0.7 : 1 }}
              >
                {loading ? <Loader2 size={20} className="animate-spin" /> : 'שלח קוד אימות'}
              </motion.button>
            ) : (
              <>
                <div>
                  <FormLabel text="קוד אימות" helpText={parentRegistrationHelpTexts.otp.tooltip} />
                  <input
                    type="text"
                    placeholder="הזן קוד בן 6 ספרות"
                    value={data.otp}
                    onChange={(e) => setData({ ...data, otp: e.target.value.slice(0, 6) })}
                    style={{ ...inputStyle, direction: 'ltr', textAlign: 'center', letterSpacing: '8px', fontSize: '24px' }}
                    maxLength={6}
                  />
                  <HelperText text={parentRegistrationHelpTexts.otp.helper} />
                </div>

                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={verifyOtp}
                  disabled={loading}
                  style={{ ...buttonStyle, opacity: loading ? 0.7 : 1 }}
                >
                  {loading ? <Loader2 size={20} className="animate-spin" /> : 'אימות וכניסה'}
                </motion.button>

                <button
                  onClick={() => countdown === 0 && sendOtp()}
                  disabled={countdown > 0}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: countdown > 0 ? colors.textLight : colors.primary,
                    fontSize: '14px',
                    cursor: countdown > 0 ? 'default' : 'pointer',
                    textAlign: 'center',
                  }}
                >
                  {countdown > 0 ? `שלח שוב בעוד ${countdown} שניות` : 'שלח קוד חדש'}
                </button>
              </>
            )}
          </div>
        )}

        {/* Step 2: Personal Details */}
        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Show invitation info if present */}
            {pendingInvitation && (
              <div style={{
                padding: '16px',
                borderRadius: '12px',
                backgroundColor: colors.secondaryLight,
                border: `2px solid ${colors.secondary}`,
                marginBottom: '8px'
              }}>
                <p style={{ fontSize: '14px', color: colors.secondary, fontWeight: '600', marginBottom: '8px' }}>
                  הוזמנת על ידי {pendingInvitation.inviterName}
                </p>
                {pendingInvitation.childrenNames && pendingInvitation.childrenNames.length > 0 && (
                  <p style={{ fontSize: '13px', color: colors.text }}>
                    לצפייה בילדים: {pendingInvitation.childrenNames.join(', ')}
                  </p>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <FormLabel text="שם פרטי" required helpText={parentRegistrationHelpTexts.firstName.tooltip} />
                <input
                  type="text"
                  placeholder="שם פרטי"
                  value={data.firstName}
                  onChange={(e) => setData({ ...data, firstName: e.target.value })}
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <FormLabel text="שם משפחה" required helpText={parentRegistrationHelpTexts.lastName.tooltip} />
                <input
                  type="text"
                  placeholder="שם משפחה"
                  value={data.lastName}
                  onChange={(e) => setData({ ...data, lastName: e.target.value })}
                  style={inputStyle}
                />
              </div>
            </div>

            <div>
              <FormLabel text="כתובת מייל (אופציונלי)" helpText={parentRegistrationHelpTexts.email.tooltip} />
              <input
                type="email"
                placeholder="example@email.com"
                value={data.email}
                onChange={(e) => setData({ ...data, email: e.target.value })}
                style={{ ...inputStyle, direction: 'ltr' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              {/* Only show back button if user didn't come from login flow */}
              {!phoneVerified && (
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setStep(1)}
                  style={{
                    ...buttonStyle,
                    flex: 1,
                    backgroundColor: 'white',
                    color: colors.text,
                    border: `2px solid ${colors.border}`,
                  }}
                >
                  <ArrowRight size={18} />
                  חזרה
                </motion.button>
              )}
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={() => setStep(3)}
                disabled={!data.firstName || !data.lastName}
                style={{
                  ...buttonStyle,
                  flex: phoneVerified ? 1 : 2,
                  opacity: !data.firstName || !data.lastName ? 0.5 : 1,
                }}
              >
                המשך
                <ArrowLeft size={18} />
              </motion.button>
            </div>
          </div>
        )}

        {/* Step 3: Preferences */}
        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Language */}
            <div>
              <FormLabel text="שפת המערכת" helpText={parentRegistrationHelpTexts.language.tooltip} />
              <div style={{ display: 'flex', gap: '10px' }}>
                {[
                  { value: 'he', label: 'עברית' },
                  { value: 'ar', label: 'ערבית' },
                  { value: 'en', label: 'English' },
                ].map((lang) => (
                  <motion.button
                    key={lang.value}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setData({ ...data, preferredLanguage: lang.value })}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '10px',
                      border: `2px solid ${data.preferredLanguage === lang.value ? colors.primary : colors.border}`,
                      backgroundColor: data.preferredLanguage === lang.value ? colors.primaryLight : 'white',
                      color: data.preferredLanguage === lang.value ? colors.primary : colors.text,
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                  >
                    {lang.label}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Notification Method */}
            <div>
              <FormLabel text="שיטת קבלת התראות" helpText={parentRegistrationHelpTexts.notificationMethod.tooltip} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  { value: 'whatsapp', label: 'WhatsApp', desc: 'מומלץ - הכי מהיר' },
                  { value: 'sms', label: 'SMS', desc: 'הודעת טקסט רגילה' },
                  { value: 'push', label: 'התראות באפליקציה', desc: 'Push notifications' },
                  { value: 'email', label: 'אימייל', desc: 'לכתובת המייל שהזנת' },
                ].map((method) => (
                  <motion.button
                    key={method.value}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setData({ ...data, notificationMethod: method.value })}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '14px 16px',
                      borderRadius: '12px',
                      border: `2px solid ${data.notificationMethod === method.value ? colors.primary : colors.border}`,
                      backgroundColor: data.notificationMethod === method.value ? colors.primaryLight : 'white',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        color: data.notificationMethod === method.value ? colors.primary : colors.text,
                        fontWeight: '500',
                      }}>
                        {method.label}
                      </div>
                      <div style={{ fontSize: '12px', color: colors.textLight }}>{method.desc}</div>
                    </div>
                    {data.notificationMethod === method.value && (
                      <CheckCircle2 size={20} color={colors.primary} />
                    )}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Address (Optional) */}
            <div>
              <FormLabel text="כתובת (אופציונלי)" helpText={parentRegistrationHelpTexts.address.tooltip} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <input
                  type="text"
                  placeholder="עיר"
                  value={data.address.city}
                  onChange={(e) => setData({ ...data, address: { ...data.address, city: e.target.value } })}
                  style={inputStyle}
                />
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    placeholder="רחוב"
                    value={data.address.street}
                    onChange={(e) => setData({ ...data, address: { ...data.address, street: e.target.value } })}
                    style={{ ...inputStyle, flex: 2 }}
                  />
                  <input
                    type="text"
                    placeholder="מס׳"
                    value={data.address.houseNumber}
                    onChange={(e) => setData({ ...data, address: { ...data.address, houseNumber: e.target.value } })}
                    style={{ ...inputStyle, flex: 1 }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    placeholder="קומה"
                    value={data.address.floor}
                    onChange={(e) => setData({ ...data, address: { ...data.address, floor: e.target.value } })}
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <input
                    type="text"
                    placeholder="דירה"
                    value={data.address.apartment}
                    onChange={(e) => setData({ ...data, address: { ...data.address, apartment: e.target.value } })}
                    style={{ ...inputStyle, flex: 1 }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={() => setStep(2)}
                style={{
                  ...buttonStyle,
                  flex: 1,
                  backgroundColor: 'white',
                  color: colors.text,
                  border: `2px solid ${colors.border}`,
                }}
              >
                <ArrowRight size={18} />
                חזרה
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={createUser}
                disabled={loading}
                style={{ ...buttonStyle, flex: 2, opacity: loading ? 0.7 : 1 }}
              >
                {loading ? <Loader2 size={20} className="animate-spin" /> : 'המשך'}
                {!loading && <ArrowLeft size={18} />}
              </motion.button>
            </div>
          </div>
        )}

        {/* Step 4: Agreements */}
        {step === 4 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div
              style={{
                backgroundColor: colors.background,
                borderRadius: '12px',
                padding: '16px',
                maxHeight: '400px',
                overflowY: 'auto',
                fontSize: '13px',
                lineHeight: '1.6',
                color: colors.textLight,
              }}
            >
              {loadingAgreement ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Loader2 size={24} className="animate-spin" style={{ margin: '0 auto' }} />
                  <p style={{ marginTop: '8px' }}>טוען הסכם...</p>
                </div>
              ) : agreementContent.length > 0 ? (
                agreementContent.map((agreement, idx) => (
                  <div key={idx}>
                    {idx > 0 && <hr style={{ margin: '16px 0', borderColor: colors.border }} />}
                    <h3 style={{ color: colors.text, marginBottom: '8px' }}>{agreement.title}</h3>
                    <div style={{ whiteSpace: 'pre-wrap' }}>
                      {agreement.content.split('\n').map((line, i) => {
                        if (line.startsWith('# ')) return <h3 key={i} style={{ color: colors.text, marginTop: '12px', marginBottom: '6px', fontSize: '15px', fontWeight: 'bold' }}>{line.replace('# ', '')}</h3>;
                        if (line.startsWith('## ')) return <h4 key={i} style={{ color: colors.text, marginTop: '10px', marginBottom: '4px', fontSize: '14px', fontWeight: '600' }}>{line.replace('## ', '')}</h4>;
                        if (line.startsWith('- ')) return <li key={i} style={{ marginRight: '16px' }}>{line.replace('- ', '')}</li>;
                        if (line.trim() === '') return <br key={i} />;
                        return <p key={i} style={{ marginBottom: '2px' }}>{line}</p>;
                      })}
                    </div>
                  </div>
                ))
              ) : (
                <>
                  <h3 style={{ color: colors.text, marginBottom: '8px' }}>תנאי שימוש</h3>
                  <p>טוען תוכן...</p>
                </>
              )}
            </div>

            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                padding: '12px',
                borderRadius: '12px',
                border: `2px solid ${data.agreementsAccepted ? colors.primary : colors.border}`,
                backgroundColor: data.agreementsAccepted ? colors.primaryLight : 'white',
              }}
            >
              <input
                type="checkbox"
                checked={data.agreementsAccepted}
                onChange={(e) => setData({ ...data, agreementsAccepted: e.target.checked })}
                style={{ width: '20px', height: '20px', accentColor: colors.primary }}
              />
              <span style={{ fontSize: '14px', color: colors.text }}>
                קראתי ואני מסכים/ה לתנאי השימוש ולמדיניות הפרטיות
              </span>
            </label>

            <div style={{ display: 'flex', gap: '12px' }}>
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={() => setStep(3)}
                style={{
                  ...buttonStyle,
                  flex: 1,
                  backgroundColor: 'white',
                  color: colors.text,
                  border: `2px solid ${colors.border}`,
                }}
              >
                <ArrowRight size={18} />
                חזרה
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={acceptAgreements}
                disabled={loading || !data.agreementsAccepted}
                style={{
                  ...buttonStyle,
                  flex: 2,
                  opacity: loading || !data.agreementsAccepted ? 0.5 : 1,
                  backgroundColor: colors.success,
                }}
              >
                {loading ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <>
                    <FileText size={18} />
                    אישור והרשמה
                  </>
                )}
              </motion.button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
